const BRAZIL_COUNTRY_CODE = "55";

/**
 * Remove o código do país (DDI) de um número quando presente.
 * Um número brasileiro local tem 10 (fixo) ou 11 (celular) dígitos.
 * Com o DDI 55 ele tem 12 ou 13 — então só removemos o "55" inicial
 * quando o comprimento indica que há código de país, evitando confundir
 * com o DDD 55 (Rio Grande do Sul).
 */
const stripBrazilCountryCode = (digits: string): string =>
  digits.length > 11 && digits.startsWith(BRAZIL_COUNTRY_CODE)
    ? digits.slice(2)
    : digits;

const formatPhone = (number: string | null | undefined): string => {
  if (!number) return "";

  const digits = stripBrazilCountryCode(number.replace(/\D/g, "")).slice(0, 11);

  if (digits.length < 10) return number;

  const ddd = digits.slice(0, 2);
  const first = digits.length === 10 ? digits.slice(2, 6) : digits.slice(2, 7);
  const second = digits.length === 10 ? digits.slice(6) : digits.slice(7);

  return `(${ddd})${first}-${second}`;
};

/**
 * Formato de leitura: `(31)9.8779-9746` — o nove do celular separado por ponto,
 * que é como o lojista confere o número de bate-pronto.
 *
 * Existe separado de `formatPhone` porque aquele é usado como **máscara
 * enquanto se digita** (input de WhatsApp, edição de cliente): um separador a
 * mais no meio da digitação faz o cursor pular de casa.
 */
const formatPhoneDisplay = (number: string | null | undefined): string => {
  if (!number) return "";

  const digits = stripBrazilCountryCode(number.replace(/\D/g, "")).slice(0, 11);

  if (digits.length < 10) return number;

  const ddd = digits.slice(0, 2);

  // Fixo não tem o nove: mantém (31)3222-1234.
  if (digits.length === 10) {
    return `(${ddd})${digits.slice(2, 6)}-${digits.slice(6)}`;
  }

  return `(${ddd})${digits.slice(2, 3)}.${digits.slice(3, 7)}-${digits.slice(7)}`;
};

/**
 * Máscara progressiva para digitação de celular/fixo brasileiro:
 * `(11) 99999-9999` se forma a cada tecla. `formatPhone` só formata a partir de
 * 10 dígitos, então enquanto se digita o campo ficaria "cru".
 */
const maskPhone = (value: string): string => {
  const d = stripBrazilCountryCode(value.replace(/\D/g, "")).slice(0, 11);
  if (d.length === 0) return "";
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10)
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

function cleanPhoneNumber(number: string): string {
  return number.replace(/\D/g, "");
}

export const phoneUtils = {
  formatPhone,
  formatPhoneDisplay,
  maskPhone,
  cleanPhoneNumber,
};
